import { plural } from '../../../core/format/text';
import { fullDate, fromIso, today } from '../../../core/format/dates';
import { isKuurExpired, prepaidLeft } from '../../../core/rules/kuur';
import type { Course } from '../../../core/types/account';
type Props = {
  course: Course;
  bookedSessions: number[];
  name: string;
};

/** Kuur als rij stippen: gehad, ingepland, nog te plannen. */
export function CourseProgress({
  course,
  bookedSessions,
  name
}: Props) {
  const booked = bookedSessions.filter(n => n > course.done).length;
  const open = Math.max(0, course.total - course.done - booked);
  return <div data-id="element-5893" data-mp="22ywhk5uhpzn4">
      <p className="flex items-baseline justify-between gap-3 text-[14px]" data-id="element-5894" data-mp="22zof3m7ne7g1">
        <span className="text-ink" data-id="element-5895" data-mp="1y8g4imul0g1a">Kuur {name}</span>
        <span className="tabular-nums text-muted" data-id="element-5896" data-mp="1y7o9bd4wckbj">
          {course.done} van {course.total} gehad
        </span>
      </p>
      {/* De stippen zijn beeld; de zin eronder zegt hetzelfde voor schermlezers. */}
      <p className="sr-only" data-id="element-5897" data-mp="15j95znv4nqg1">
        {plural(course.done, 'sessie', 'sessies')} gehad, {booked} ingepland, {open} nog te plannen.
      </p>
      <ol className="mt-3 flex gap-1.5" aria-hidden="true" data-id="element-5898" data-mp="3ilocmsiru2oe">
        {Array.from({
        length: course.total
      }, (_, i) => {
        const n = i + 1;
        const done = n <= course.done;
        const isBooked = bookedSessions.includes(n);
        return <li key={n} title={done ? `Sessie ${n}: gehad` : isBooked ? `Sessie ${n}: ingepland` : `Sessie ${n}: nog plannen`} className={`h-2 flex-1 rounded-full ${done ? 'bg-ink' : isBooked ? 'bg-clay' : 'bg-sand ring-1 ring-inset ring-line'}`} data-id="element-5899" data-mp="3rd6sf2bt2ggy" />;
      })}
      </ol>
      <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted" aria-hidden="true" data-id="element-5900" data-mp="38nh0qo3caat9">
        <span className="flex items-center gap-1.5" data-id="element-5901" data-mp="iy6ldw7ti8lq">
          <span className="h-1.5 w-1.5 rounded-full bg-ink" data-id="element-5902" data-mp="ixn3gfi63k7p" /> Gehad
        </span>
        <span className="flex items-center gap-1.5" data-id="element-5903" data-mp="nw860lkw6fzb">
          <span className="h-1.5 w-1.5 rounded-full bg-clay" data-id="element-5904" data-mp="nz2m60wvhemx" /> Ingepland
        </span>
        {open > 0 && <span className="flex items-center gap-1.5" data-id="element-5905" data-mp="2fz2p4hwqltrb">
            <span className="h-1.5 w-1.5 rounded-full bg-sand ring-1 ring-inset ring-line" data-id="element-5906" data-mp="2fy9iri1ox69p" /> Nog plannen
          </span>}
      </p>
      {course.prepaid && <PrepaidLine course={course} data-id="element-5907" data-mp="2oyz4tu15fia8" />}
    </div>;
}
function PrepaidLine({
  course
}: {
  course: Course;
}) {
  const now = today();
  const p = course.prepaid;
  if (!p) return null;
  const left = prepaidLeft(course, now);
  const until = fullDate(fromIso(p.validUntil));
  return <p className={`mt-2 text-[13px] ${isKuurExpired(course, now) ? 'text-clay' : 'text-muted'}`} data-id="element-5908" data-mp="2azpuvc3jdkep">
      {isKuurExpired(course, now) ? `Vooruit betaald; verlopen op ${until}.` : left > 0 ? `Vooruit betaald · nog ${plural(left, 'sessie', 'sessies')} · geldig tot ${until}` : 'Vooruit betaald · alle sessies gehad'}
    </p>;
}